/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import template from './context-service-configuration-edit-page.component.html';
import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {IAB_TAXONOMY} from '../../../components/util/iab-taxonomy';
import {ContextServiceConfigurationResource} from '../../../components/api/resources/resource/context-service-configuration.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {WINDOW} from '../../angular-platform-providers';

@Component({
  standalone: false,
  selector: 'adn-context-service-configuration-edit-page',
  template
})
export class ContextServiceConfigurationEditPageComponent extends AdminResourceEditPageBase {
  iabTaxonomy: any[] = _.cloneDeep(IAB_TAXONOMY);
  vm: any = {
    keywordMetaTags: '',
    categoryMetaTags: '',
    deriveKeywordsFromUrl: false,
    categoryKeywords: [],
    selectorConfiguration: [],
    trackedKeywords: []
  };
  detailsPref = false;
  isLoading = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(ContextServiceConfigurationResource) private readonly ContextServiceConfiguration: any,
    @Inject(WINDOW) private readonly window: any,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
  }

  get visibleTrackedKeywords(): any[] {
    return _.filter(this.vm.trackedKeywords || [], (trackedKeyword) => !trackedKeyword.deleted);
  }

  protected afterInitEditingModel(): void {
    this.hydrateVm();
  }

  protected afterInitAfterSave(): void {
    this.hydrateVm();
  }

  protected onBeforeSubmit(): void {
    this.editingModel.teamMappings = _.map(_.keyBy(_.filter(this.editingModel.teamMappings, (tm) => {
      return tm.team && tm.team.id && _.trim(tm.siteGroup).length > 0;
    }), 'team.id'), (tm) => tm);

    this.editingModel.crawlerConfig = this.editingModel.crawlerConfig || {};
    this.editingModel.crawlerConfig.keywordMetaTags = _.isString(this.vm.keywordMetaTags)
      ? this.vm.keywordMetaTags.split(',').map((tag: string) => tag.trim())
      : [];
    this.editingModel.crawlerConfig.categoryMetaTags = _.isString(this.vm.categoryMetaTags)
      ? this.vm.categoryMetaTags.split(',').map((tag: string) => tag.trim())
      : [];
    this.editingModel.crawlerConfig.deriveKeywordsFromUrl = _.isBoolean(this.vm.deriveKeywordsFromUrl)
      ? this.vm.deriveKeywordsFromUrl
      : false;
    this.editingModel.crawlerConfig.categoryKeywords = {};
    this.editingModel.crawlerConfig.trackedKeywords = _.isArray(this.vm.trackedKeywords)
      ? this.vm.trackedKeywords
        .filter((trackedKeyword: any) => !(trackedKeyword.deleted || false))
        .filter((trackedKeyword: any) => trackedKeyword.keyword.trim().length)
        .map((trackedKeyword: any) => this.sanitiseVmTrackedKeywords(trackedKeyword))
      : [];
    (this.vm.categoryKeywords || []).forEach((categoryKeyword: any) => {
      if (categoryKeyword.category) {
        this.editingModel.crawlerConfig.categoryKeywords[categoryKeyword.category] = (categoryKeyword.keywords || '')
          .split(',').map((keyword: string) => keyword.trim()).filter((keyword: string) => keyword.length > 0);
      }
    });
    this.editingModel.crawlerConfig.selectorConfiguration = {};
    (this.vm.selectorConfiguration || []).forEach((selector: any) => {
      if (selector.domain) {
        if (selector.value?.isJavaScript) {
          delete selector.value.excludeSelector;
        }
        this.editingModel.crawlerConfig.selectorConfiguration[selector.domain] = (selector.value || {});
      }
    });
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  toggleKeyword(keyword: any): void {
    keyword.showTextArea = !keyword.showTextArea;
    keyword.showTextAreaRows = Math.max(parseInt(String(keyword.keywords.length / 150), 10), 4);
  }

  addIabCategory(): void {
    this.vm.categoryKeywords = this.vm.categoryKeywords || [];
    this.vm.categoryKeywords.push({category: '', keywords: ''});
    this.markExternalFormDirty();
  }

  deleteIabCategory(index: number): void {
    _.pullAt(this.vm.categoryKeywords, index);
    this.markExternalFormDirty();
  }

  addSelector(): void {
    this.vm.selectorConfiguration = this.vm.selectorConfiguration || [];
    this.vm.selectorConfiguration.push({domain: '', value: {isJavaScript: false}});
    this.markExternalFormDirty();
  }

  deleteSelector(index: number): void {
    _.pullAt(this.vm.selectorConfiguration, index);
    this.markExternalFormDirty();
  }

  toggleEditTrackedKeyword(index: number): void {
    _.forEach(this.vm.trackedKeywords, (trackedKeyword: any, i: any) => {
      trackedKeyword.edited = Number(i) === index ? !trackedKeyword.edited : false;
    });
    this.markExternalFormDirty();
  }

  addTrackedKeyword(): void {
    this.vm.trackedKeywords = this.vm.trackedKeywords || [];
    this.vm.trackedKeywords.unshift({
      keyword: '',
      mappings: '',
      edited: true
    });
    this.markExternalFormDirty();
  }

  removeTrackedKeyword(trackedKeyword: any): void {
    this.markExternalFormDirty();
    trackedKeyword.edited = false;
    trackedKeyword.deleted = true;
  }

  downloadTrackedKeywordsCsv(): void {
    const rows = this.getRows();
    const header = 'keyword,mappings';
    const body = rows.map((row) => {
      const mappings = _.isArray(row.mappings) ? row.mappings.join(',') : (row.mappings || '');
      return [this.escapeCsvValue(row.keyword), this.escapeCsvValue(mappings)].join(',');
    }).join('\r\n');
    const csv = '\uFEFF' + header + '\r\n' + body;
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = this.window.URL.createObjectURL(blob);
    const link = this.window.document.createElement('a');
    link.href = url;
    link.download = 'Adnuntius-Tracked-Keywords.csv';
    this.window.document.body.appendChild(link);
    link.click();
    this.window.document.body.removeChild(link);
    this.window.URL.revokeObjectURL(url);
  }

  uploadHook(): void {
    this.isLoading = true;
    this.ContextServiceConfiguration.get({id: this.editingModel.id}).$promise.then((data: any) => {
      this.ngZone.run(() => {
        const resource = data.results ? _.head(data.results) : data;
        if (resource) {
          this.applySave(resource);
        }
        this.isLoading = false;
      });
    }, () => {
      this.ngZone.run(() => {
        this.isLoading = false;
      });
    });
  }

  private getRows(): any[] {
    return _.map(this.vm.trackedKeywords || [], (trackedKeyword) => this.sanitiseVmTrackedKeywords(trackedKeyword));
  }

  private sanitiseVmTrackedKeywords(trackedKeyword: any): any {
    const mapping = (trackedKeyword.mappings || '').match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g);
    return {
      keyword: trackedKeyword.keyword,
      mappings: mapping ? mapping.map((entry: string) => entry.trim()) : []
    };
  }

  private escapeCsvValue(value: any): string {
    const text = value === null || value === undefined ? '' : String(value);
    if (/[",\n\r]/.test(text)) {
      return '"' + text.replace(/"/g, '""') + '"';
    }
    return text;
  }

  private hydrateVm(): void {
    this.editingModel.contextService = this.editingModel.contextService || 'ADNUNTIUS';

    this.vm = this.vm || {};
    this.vm.keywordMetaTags = _.get(this.editingModel, ['crawlerConfig', 'keywordMetaTags'], []).join(', ');
    this.vm.categoryMetaTags = _.get(this.editingModel, ['crawlerConfig', 'categoryMetaTags'], []).join(', ');
    this.vm.deriveKeywordsFromUrl = _.get(this.editingModel, ['crawlerConfig', 'deriveKeywordsFromUrl'], false);
    this.vm.categoryKeywords = [];
    _.forEach(_.get(this.editingModel, ['crawlerConfig', 'categoryKeywords']), (val, key) => {
      this.vm.categoryKeywords.push({category: key, keywords: (val || []).join(', ')});
    });
    this.vm.categoryKeywords.sort((a: any, b: any) => {
      const aEntry = IAB_TAXONOMY.find((entry) => entry.id === a.category);
      const bEntry = IAB_TAXONOMY.find((entry) => entry.id === b.category);
      return (aEntry && aEntry.order) > (bEntry && bEntry.order) ? 1 : -1;
    });
    this.vm.selectorConfiguration = [];
    _.forEach(_.get(this.editingModel, ['crawlerConfig', 'selectorConfiguration']), (val, key) => {
      this.vm.selectorConfiguration.push({domain: key, value: (val || {})});
    });
    this.vm.selectorConfiguration.sort((a: any, b: any) => String(a.domain).localeCompare(String(b.domain)));
    this.vm.trackedKeywords = (_.get(this.editingModel, ['crawlerConfig', 'trackedKeywords'], []) || []).map((trackedKeyword: any) => {
      return {
        keyword: trackedKeyword.keyword,
        mappings: (trackedKeyword.mappings || []).map((mapping: string) => {
          if (mapping.includes(',') && !mapping.startsWith('"')) {
            return `"${mapping}"`;
          }
          return mapping;
        }).join(', ')
      };
    });
  }
}
